import React, { useState, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

interface MultiEmailInputProps {
  handleAddClick: (email: string) => void;
  handleRemoveClick: (email: string) => void;
}

const MultiEmailInput: React.FC<MultiEmailInputProps> = ({
  handleAddClick,
  handleRemoveClick,
}) => {
  const { t } = useTranslation('team');
  const [emails, setEmails] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState('');

  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const addEmails = (emailInput: string) => {
    if (!emailInput.includes(',')) {
      const trimmedEmail = emailInput.trim();
      if (isValidEmail(trimmedEmail)) {
        setEmails(prevEmails => [...new Set([...prevEmails, trimmedEmail])]);
        handleAddClick(trimmedEmail);
        setInputValue('');
      }
      return;
    }

    const newEmails = emailInput
      .split(',')
      .map(email => email.trim())
      .filter(email => email && isValidEmail(email));

    if (newEmails.length > 0) {
      setEmails(prevEmails => [...new Set([...prevEmails, ...newEmails])]);
      newEmails.forEach(email => handleAddClick(email));
      setInputValue('');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    if (newValue.endsWith(',')) {
      addEmails(newValue);
    } else {
      setInputValue(newValue);
    }
  };

  const handleInputKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (inputValue) {
        addEmails(inputValue);
      }
    } else if (
      e.key === 'Backspace' &&
      inputValue === '' &&
      emails.length > 0
    ) {
      e.preventDefault();
      const updatedEmails = [...emails];
      const removedEmail = updatedEmails.pop()!;
      setEmails(updatedEmails);
      handleRemoveClick(removedEmail);
    }
  };

  const handleBlur = () => {
    if (inputValue) {
      addEmails(inputValue);
    }
  };

  return (
    <div className="w-full">
      <label
        htmlFor="email-input"
        className="block text-sm  text-gray-500 mb-1 font-normal"
      >
        {t('email_label')}
      </label>
      <div className="flex flex-col gap-2 p-2 border rounded-md bg-white">
        <div className="flex flex-wrap gap-2">
          {emails.map(email => (
            <div
              key={email}
              className="flex items-center bg-gray-100 text-gray-800 text-sm rounded-full px-3 py-1"
            >
              <span>{email}</span>
              <button
                onClick={() => {
                  handleRemoveClick(email);
                  setEmails(emails.filter(e => e !== email));
                }}
                className="ml-2 focus:outline-none"
                aria-label={t('remove_email_aria_label', { email })}
              >
                ×
              </button>
            </div>
          ))}
        </div>
        <textarea
          id="email-input"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
          onBlur={handleBlur}
          className="w-full text-sm resize-none outline-none min-h-[60px]"
          placeholder={t('textarea_placeholder')}
          aria-label={t('textarea_aria_label')}
          rows={3}
        />
      </div>
    </div>
  );
};

export default MultiEmailInput;
